import React from "react";
import Header from "../components/Header";

const SmartSpool = () => {
  return (
    <><Header />
    <div className="w-full min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 p-4 md:p-8 lg:p-10 font-sans">
      
      {/* Main Container */}
      <div className="max-w-7xl mx-auto">
        {/* Enhanced Header */}
        <div className="flex items-center gap-4 md:gap-6 mb-8">
          <div className="relative">
            <div className="w-12 h-24 bg-gradient-to-b from-blue-500 to-blue-800 rounded-xl shadow-lg"></div>
            <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-orange-500 rounded-full border-4 border-white"></div>
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-900 to-blue-600 bg-clip-text text-transparent">
              Smart Spool System
            </h1>
            <p className="text-sm md:text-base text-blue-700 mt-2">
              AI-Powered Engineering Drawing Conversion & Automation Platform
            </p>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">

          {/* LEFT CONTENT - Enhanced */}
          <div className="space-y-8">

            {/* Enhanced System Description Card */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-2xl shadow-2xl p-6 md:p-8 overflow-hidden relative">
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-800/30 rounded-full -translate-y-8 translate-x-8"></div>
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-6">
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                  </svg>
                  <h2 className="text-2xl font-bold">Intelligent Automation System</h2>
                </div>

                <p className="text-base md:text-lg leading-relaxed">
                  Smart Spool is an advanced AI-powered automation platform that rapidly converts engineering
                  isometric drawings into fabrication-ready spool drawings with 99.5% accuracy.
                  It streamlines drafting, eliminates manual errors, and accelerates the entire spool
                  generation workflow for EPC and Oil & Gas projects through intelligent pattern recognition.
                </p>
                
                <div className="flex flex-wrap gap-3 mt-6">
                  <span className="px-4 py-2 bg-blue-600 rounded-full text-sm font-medium">AI-Powered Conversion</span>
                  <span className="px-4 py-2 bg-blue-600 rounded-full text-sm font-medium">Rule-Based Validation</span>
                  <span className="px-4 py-2 bg-blue-600 rounded-full text-sm font-medium">Auto MTO Generation</span>
                </div>
              </div>
            </div>

            {/* Enhanced Features Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl shadow-md border border-blue-100 hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-800 mb-2">Intelligent Drawing Conversion</h3>
                <p className="text-sm text-gray-600">AI-driven conversion with pattern recognition</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl shadow-md border border-blue-100 hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 00-1.414 1.414l1.414 1.414a1 1 0 001.414-1.414l1.293-1.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-800 mb-2">Automated Validation</h3>
                <p className="text-sm text-gray-600">Smart rule-based error detection</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl shadow-md border border-blue-100 hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-gray-800 mb-2">Complete Documentation</h3>
                <p className="text-sm text-gray-600">Auto-generated MTO and weld maps</p>
              </div>
            </div>

            {/* Enhanced Bullet Points */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-blue-900 mb-6 flex items-center gap-3">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Key Benefits & Advantages
              </h3>
              
              <div className="space-y-5">
                {[
                  {
                    title: "Rapid Production",
                    desc: "Enables rapid production of fabrication-ready drawings with 80% faster turnaround.",
                    icon: "⚡"
                  },
                  {
                    title: "Error Elimination",
                    desc: "Eliminates manual data entry errors through smart rule‑based validation, reducing time by 60–80%.",
                    icon: "✅"
                  },
                  {
                    title: "Complete MTO Generation",
                    desc: "Generates comprehensive MTO with weld maps, cut lengths, and full material traceability.",
                    icon: "📋"
                  },
                  {
                    title: "Cost Savings",
                    desc: "Lower drafting hours and reduced design errors result in measurable cost savings of 30-40%.",
                    icon: "💰"
                  }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4 group cursor-pointer">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <span className="text-lg">{item.icon}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-1">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT CONTENT - Enhanced */}
          <div className="space-y-8">

            {/* Laptop Dashboard */}
            <div className="relative">
              <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-4 px-6 rounded-t-2xl font-bold">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
                  </svg>
                  Smart Spool Dashboard Interface
                </div>
              </div>
              <div className="p-4 bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-b-2xl shadow-xl">
                <div className="relative">
                  <img
                    src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                    alt="Smart Spool Dashboard Interface"
                    className="w-full h-72 object-cover rounded-lg shadow-md"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent rounded-lg"></div>
                </div>
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Real-time dashboard showing drawing conversion progress, validation results, and project status with interactive controls.
                  </p>
                </div>
              </div>
            </div>

            {/* Process Flow Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-orange-50 to-white p-5 rounded-xl shadow-md border border-orange-100 hover:shadow-lg transition-shadow">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-orange-600 font-bold">1</span>
                </div>
                <h4 className="font-bold text-gray-800 mb-2">Drawing Upload</h4>
                <p className="text-xs text-gray-600">Upload isometric drawings in multiple formats</p>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl shadow-md border border-blue-100 hover:shadow-lg transition-shadow">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-blue-600 font-bold">2</span>
                </div>
                <h4 className="font-bold text-gray-800 mb-2">AI Processing</h4>
                <p className="text-xs text-gray-600">Automatic conversion and validation</p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-white p-5 rounded-xl shadow-md border border-green-100 hover:shadow-lg transition-shadow">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                  <span className="text-green-600 font-bold">3</span>
                </div>
                <h4 className="font-bold text-gray-800 mb-2">Output Generation</h4>
                <p className="text-xs text-gray-600">Fabrication-ready drawings & MTO</p>
              </div>
            </div>

            {/* Enhanced Side Images */}
            <div className="space-y-6">
              {/* Before/After Comparison */}
              <div className="bg-white rounded-2xl shadow-lg p-5 border border-gray-200">
                <h4 className="font-bold text-gray-800 mb-4 text-center">Transformation Results</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-red-600 mb-2 text-center">Before</div>
                    <img
                      src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                      alt="Manual Drawing Process"
                      className="w-full h-32 object-cover rounded-lg shadow-sm"
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-green-600 mb-2 text-center">After</div>
                    <img
                      src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
                      alt="Automated Output"
                      className="w-full h-32 object-cover rounded-lg shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Integration Display */}
              <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-xl p-5 shadow-lg">
                <h4 className="font-bold mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                  </svg>
                  System Integration
                </h4>
                <p className="text-sm opacity-90">
                  Seamlessly integrates with CAD systems, ERP platforms, and project management tools for end-to-end workflow automation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-2xl shadow-md border border-blue-100 text-center">
            <div className="text-3xl font-bold text-blue-800">80%</div>
            <div className="text-sm text-gray-600 mt-2">Time Reduction</div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-md border border-blue-100 text-center">
            <div className="text-3xl font-bold text-blue-800">99.5%</div>
            <div className="text-sm text-gray-600 mt-2">Accuracy Rate</div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-md border border-blue-100 text-center">
            <div className="text-3xl font-bold text-blue-800">40%</div>
            <div className="text-sm text-gray-600 mt-2">Cost Savings</div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-md border border-blue-100 text-center">
            <div className="text-3xl font-bold text-blue-800">5000+</div>
            <div className="text-sm text-gray-600 mt-2">Projects Automated</div>
          </div>
        </div>

        {/* Technology Partners */}
        <div className="mt-12 bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-200">
          <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">Compatible With</h3>
          <div className="flex flex-wrap justify-center gap-8">
            <div className="w-20 h-20 bg-white rounded-xl shadow-sm flex items-center justify-center">
              <span className="font-bold text-blue-700">AutoCAD</span>
            </div>
            <div className="w-20 h-20 bg-white rounded-xl shadow-sm flex items-center justify-center">
              <span className="font-bold text-blue-700">SolidWorks</span>
            </div>
            <div className="w-20 h-20 bg-white rounded-xl shadow-sm flex items-center justify-center">
              <span className="font-bold text-blue-700">Revit</span>
            </div>
            <div className="w-20 h-20 bg-white rounded-xl shadow-sm flex items-center justify-center">
              <span className="font-bold text-blue-700">SAP</span>
            </div>
            <div className="w-20 h-20 bg-white rounded-xl shadow-sm flex items-center justify-center">
              <span className="font-bold text-blue-700">Oracle</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default SmartSpool;